// The "Kanaal" chat: message stream, typing indicator, choices and free-text answers.

import { sfx } from './audio.js';
import { $, clock, el, isTouch, typingTime, wait } from './util.js';

export type Speaker = 'joris' | 'ellen' | 'unknown' | 'me';

interface SayOptions {
  /** Old messages from Ellen's history: no typing, no sound. */
  history?: boolean;
  time?: string;
  /** Extra pause before the character starts typing. */
  pause?: number;
  /** Visual style variant. */
  variant?: 'hidden';
}

const SPEAKER: Record<Speaker, { side: 'in' | 'out'; label?: string; cls?: string }> = {
  joris: { side: 'in' },
  unknown: { side: 'in', label: 'Onbekend apparaat', cls: 'msg--unknown' },
  ellen: { side: 'out', cls: 'msg--ellen' },
  me: { side: 'out' },
};

export class Chat {
  private stream = $('#chat-stream');
  private composer = $('#chat-composer');
  private status = $('#chat-status');
  private last?: { speaker: Speaker; node: HTMLElement };

  reset() {
    this.stream.replaceChildren();
    this.composer.replaceChildren();
    this.last = undefined;
    this.setStatus('laatst gezien 21 dagen geleden');
  }

  setStatus(text: string, kind: '' | 'live' | 'typing' = '') {
    this.status.textContent = text;
    this.status.className = kind ? `is-${kind}` : '';
  }

  private scroll() {
    requestAnimationFrame(() => { this.stream.scrollTop = this.stream.scrollHeight; });
  }

  private append(node: HTMLElement, speaker?: Speaker) {
    if (speaker && this.last?.speaker === speaker && this.last.node === this.stream.lastElementChild) node.classList.add('same');
    this.stream.append(node);
    this.last = speaker ? { speaker, node } : undefined;
    this.scroll();
  }

  day(label: string) { this.append(el('div.day', {}, label)); }

  system(text: string, tone: '' | 'alarm' | 'ok' = '') {
    this.append(el(`div.sys${tone ? `.sys--${tone}` : ''}`, { html: text }));
  }

  private bubble(speaker: Speaker, content: Node | string, opts: SayOptions = {}): HTMLElement {
    const s = SPEAKER[speaker];
    const showLabel = s.label && this.last?.speaker !== speaker;
    const msg = el(`div.msg.msg--${s.side}${s.cls ? `.${s.cls}` : ''}${opts.history ? '.msg--history' : ''}${opts.variant ? `.msg--${opts.variant}` : ''}`, {},
      showLabel ? el('span.msg__from', {}, s.label!) : null,
      el('div.bubble', {}, content, el('time', {}, opts.time ?? clock())),
    );
    return msg;
  }

  /** Shows the typing indicator for a while, then the message. */
  async say(speaker: Speaker, text: string, opts: SayOptions = {}) {
    if (opts.history) {
      this.append(this.bubble(speaker, text, opts), speaker);
      await wait(260);
      return;
    }
    await wait(opts.pause ?? 450);
    if (speaker === 'joris' || speaker === 'unknown') {
      const dots = el('div.msg.msg--in', {}, el('div.bubble.typing', {}, el('i'), el('i'), el('i')));
      this.stream.append(dots); this.scroll();
      if (speaker === 'joris') this.setStatus('typt…', 'typing');
      await wait(typingTime(text));
      dots.remove();
      if (speaker === 'joris') this.setStatus('online', 'live');
      sfx.incoming();
    }
    this.append(this.bubble(speaker, text, opts), speaker);
  }

  photo(speaker: Speaker, src: string, caption: string, opts: SayOptions = {}) {
    const img = el('img', { src, alt: caption, loading: 'lazy' });
    img.addEventListener('click', () => openLightbox(src));
    const fig = el('figure', { style: 'margin:0' }, img, el('figcaption', {}, caption));
    const node = this.bubble(speaker, fig, opts);
    node.classList.add('msg--photo');
    this.append(node, speaker);
    return node;
  }

  deleted(speaker: Speaker, opts: SayOptions = {}) {
    const node = this.bubble(speaker, 'Dit bericht is verwijderd.', opts);
    node.classList.add('msg--deleted');
    this.append(node, speaker);
  }

  taskCard(num: number, title: string, meta: string) {
    this.append(el('div.taskcard', {},
      el('span.taskcard__num', {}, `${num}/3`), el('span.taskcard__title', {}, title),
      el('span.taskcard__check', { 'aria-label': 'voltooid' }, '✓'), el('span.taskcard__meta', {}, meta)));
  }

  /** Offers choices; resolves with the chosen text (or `null` when `timeoutMs` passes without a choice). */
  choose(options: string[], { timeoutMs, quiet }: { timeoutMs?: number; quiet?: string } = {}): Promise<string | null> {
    return new Promise((resolve) => {
      const box = el('div.choices', { role: 'group' });
      let timer = 0;
      const done = (value: string | null, silent = false) => {
        clearTimeout(timer);
        this.composer.replaceChildren();
        if (value !== null && !silent) { this.append(this.bubble('me', value), 'me'); sfx.sent(); }
        resolve(value);
      };
      for (const o of options) {
        const b = el('button.choice', { type: 'button' }, o);
        b.addEventListener('click', () => done(o));
        box.append(b);
      }
      if (quiet) {
        const b = el('button.choice.choice--quiet', { type: 'button' }, quiet);
        b.addEventListener('click', () => done(null, true));
        box.append(b);
      }
      this.composer.replaceChildren(box);
      this.scroll();
      if (timeoutMs) timer = window.setTimeout(() => done(null, true), timeoutMs);
    });
  }

  /** Free-text answer. Resolves with the trimmed text. */
  ask(placeholder: string, { inputmode = 'text', autocomplete = 'off', maxlength = 80 } = {}): Promise<string> {
    return new Promise((resolve) => {
      const input = el('input', { type: 'text', placeholder, inputmode, autocomplete, maxlength, enterkeyhint: 'send', 'aria-label': placeholder, autocapitalize: 'sentences', spellcheck: 'false' });
      const send = el('button.send', { type: 'submit', 'aria-label': 'Verstuur', disabled: true, html: '<svg viewBox="0 0 24 24" width="20" height="20"><path fill="currentColor" d="M3.4 20.4 21 12 3.4 3.6 3.4 10l12.6 2-12.6 2z"/></svg>' });
      const form = el('form.inputrow', {}, input, send);
      input.addEventListener('input', () => { send.disabled = !input.value.trim(); });
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        const v = input.value.trim();
        if (!v) return;
        this.composer.replaceChildren();
        this.append(this.bubble('me', v), 'me');
        sfx.sent();
        resolve(v);
      });
      this.composer.replaceChildren(form);
      // Do not pop up the keyboard over the story on phones before the player looks at it
      if (!isTouch()) setTimeout(() => input.focus({ preventScroll: true }), 50);
      this.scroll();
    });
  }
}

export function openLightbox(src: string) {
  const box = $('#lightbox');
  box.querySelector('img')!.setAttribute('src', src);
  box.hidden = false;
  box.onclick = () => { box.hidden = true; };
}
